import {useEffect, useRef, useState} from "react";
import {ArrowLeft, Loader2, UserCheck} from "lucide-react";

const CODE_LENGTH = 6;

const OTPField = ({
  value,
  onChange,
  isActionLoading,
  onAction,
  onResend,
  actionLabel,
  onBack,
  resendTimer,
  loginState,
}) => {
  const inputRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);

  // Latest props via refs, so async callbacks (WebOTP) and bulk handlers
  // never act on a stale closure.
  const onChangeRef = useRef(onChange);
  const onActionRef = useRef(onAction);
  const loadingRef = useRef(isActionLoading);
  useEffect(() => {
    onChangeRef.current = onChange;
    onActionRef.current = onAction;
    loadingRef.current = isActionLoading;
  });

  // A full code delivered in ONE action (WebOTP, OS autofill, keyboard
  // clipboard chip, paste) is filled AND auto-verified -- those sources
  // don't typo, so the extra Verify tap is pure friction. The code goes to
  // onAction directly (not read back from state, which may not have
  // re-rendered yet). Typed codes still need the explicit tap: a mistyped
  // 6th digit would burn one of the 3 allowed attempts, and auto-acting on
  // a keystroke is what WCAG 3.2.2 warns about.
  const bulkEntry = (raw) => {
    const code = (raw || "").replace(/[^0-9]/g, "").slice(0, CODE_LENGTH);
    if (!code) return;
    onChangeRef.current(code);
    if (code.length === CODE_LENGTH && !loadingRef.current)
      onActionRef.current(code);
  };

  // WebOTP: on Android Chrome, ask the OS for the code from the incoming
  // SMS -- the user gets a one-tap "Allow" prompt and the field fills
  // itself. Requires the SMS to end with "@<domain> #<code>" (sent by the
  // auth-triggers lambda when WEBOTP_DOMAIN is set). No-op on iOS/desktop,
  // where OTPCredential doesn't exist.
  const bulkEntryRef = useRef(bulkEntry);
  bulkEntryRef.current = bulkEntry;
  useEffect(() => {
    if (!("OTPCredential" in window)) return undefined;
    const abort = new AbortController();
    navigator.credentials
      .get({otp: {transport: ["sms"]}, signal: abort.signal})
      .then((cred) => bulkEntryRef.current(cred?.code))
      .catch(() => {}); // dismissed, timed out, or aborted on unmount
    return () => abort.abort();
  }, []);

  // ONE real input holds the whole code; the six boxes are display-only.
  // With six separate inputs, Android Gboard delivered paste/autofill as an
  // IME composition into box 0, and the focus hop between boxes cancelled
  // the composition mid-insert -- its commit then landed in the wrong box,
  // leaving a single character or a scrambled code.

  const handleChange = (e) => {
    const inserted = (e.nativeEvent?.data || "").replace(/[^0-9]/g, "");
    // A multi-digit insertion (SMS autofill, keyboard clipboard chip) is the
    // whole code; take it alone so a stray already-typed digit can't shift it.
    if (inserted.length > 1) {
      bulkEntry(inserted);
      return;
    }
    const cleaned = e.target.value.replace(/[^0-9]/g, "");
    onChange(cleaned.slice(0, CODE_LENGTH));
  };

  const handlePaste = (e) => {
    e.preventDefault();
    bulkEntry(e.clipboardData.getData("text"));
  };

  // Editing is append/delete at the end, so the caret must stay there;
  // otherwise a tap on a middle box would silently insert mid-code.
  const moveCaretToEnd = () => {
    const el = inputRef.current;
    if (!el) return;
    requestAnimationFrame(() =>
      el.setSelectionRange(el.value.length, el.value.length),
    );
  };

  // Real <form> + type="submit" so the mobile keyboard's Go/Enter key in a
  // code box verifies exactly like tapping the action button. The guard
  // mirrors the button's disabled state for Enter during an in-flight
  // request; Resend and Back must stay type="button" or they'd submit too.
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isActionLoading && value.length === CODE_LENGTH) onAction();
  };

  const activeIdx = Math.min(value.length, CODE_LENGTH - 1);
  const isError = loginState.message_type === "error";

  const boxClasses = (i) => {
    const active = isFocused && i === activeIdx;
    if (isError)
      return active
        ? "border-red-600 bg-white ring-4 ring-red-100"
        : "border-red-500";
    return active
      ? "border-indigo-500 bg-white ring-4 ring-indigo-100"
      : "border-gray-200";
  };

  return (
    <form
      className="w-full flex-1 flex flex-col items-center"
      onSubmit={handleSubmit}
      noValidate
    >
      {/* md:flex-1 only: on mobile the stretch would push the buttons to
          the bottom of the screen, behind the iOS keyboard. */}
      <div className="w-full max-w-md space-y-2 mb-3 md:mb-6 md:flex-1 flex flex-col justify-start">
        <div>
          <label htmlFor="otp-input" className="block text-[#191919] ml-1 mb-2">
            Verification code
          </label>
          <div className="relative">
            <div
              className="flex justify-between gap-1 md:gap-2"
              aria-hidden="true"
            >
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <div
                  key={i}
                  data-testid={`otp-box-${i}`}
                  className={`w-12 h-12 md:w-16 md:h-12 flex items-center justify-center font-bold bg-slate-50 border-2 rounded-xl transition-all font-[urbanist] text-gray-700 shadow-sm ${boxClasses(i)}`}
                  style={{fontSize: "27px", lineHeight: "1"}}
                >
                  {value[i] ||
                    (isFocused && i === activeIdx && (
                      <span className="w-0.5 h-6 bg-gray-700 animate-pulse" />
                    ))}
                </div>
              ))}
            </div>
            <input
              ref={inputRef}
              id="otp-input"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete="one-time-code"
              autoFocus
              value={value}
              onChange={handleChange}
              onPaste={handlePaste}
              onFocus={() => {
                setIsFocused(true);
                moveCaretToEnd();
              }}
              onBlur={() => setIsFocused(false)}
              onClick={moveCaretToEnd}
              /* NOT opacity-0: Chrome's built-in SMS-code autofill skips
                 fields it deems invisible, so the input stays "visible"
                 with transparent ink instead. */
              className="absolute inset-0 w-full h-full border-0 outline-none bg-transparent text-transparent caret-transparent selection:bg-transparent selection:text-transparent"
              /* 16px: anything smaller makes iOS Safari zoom in on focus */
              style={{fontSize: "16px", WebkitTextFillColor: "transparent"}}
            />
          </div>
          <div className="flex justify-between items-center px-1 mt-2 md:mt-4">
            <span className="text-gray-500">
              {resendTimer > 0 ? (
                <span>
                  Resend code in <b>{resendTimer}s</b>
                </span>
              ) : (
                <button
                  type="button"
                  className="text-indigo-600 font-semibold hover:underline"
                  onClick={onResend}
                >
                  Resend Code
                </button>
              )}
            </span>
          </div>
        </div>
      </div>
      <div className="w-full max-w-md space-y-3 md:space-y-4 pb-5 md:pb-0">
        <button
          className={`w-full py-4 md:py-3 rounded-full flex items-center justify-center gap-2 text-white text-xl font-bold transition-all active:scale-[0.98] ${
            isActionLoading
              ? "bg-[#b3b3b3] cursor-wait"
              : value.length === CODE_LENGTH
                ? "bg-[#1a1a1a] hover:bg-black shadow-lg"
                : "bg-[#b3b3b3] cursor-not-allowed"
          }`}
          type="submit"
          disabled={isActionLoading || value.length < CODE_LENGTH}
        >
          {isActionLoading ? (
            <>
              <Loader2 size={20} className="animate-spin" /> Verifying...
            </>
          ) : (
            <>
              <UserCheck size={20} /> {actionLabel}
            </>
          )}
        </button>
        <button
          type="button"
          className="w-full py-4 md:py-3 rounded-full bg-[#f8faff] text-[#1a1a1a] font-bold flex items-center justify-center gap-2 hover:bg-blue-50"
          onClick={onBack}
        >
          <ArrowLeft size={20} /> Back
        </button>
      </div>
    </form>
  );
};

export default OTPField;
